import { Box, Card, CardContent, Divider, Typography } from '@mui/material'
import { PASS_META } from '../../types'

const PHASES = [
  { n: 1, label: 'Prompt Engineering and Knowledge Unit Extraction', active: true },
  { n: 2, label: 'Knowledge Graph Construction', active: false },
  { n: 3, label: 'Learning Graph Modeling', active: false },
  { n: 4, label: 'Instructional Design Workbench', active: false },
]

export default function AboutPage() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.default' }}>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 4 }}>
        {/* Header */}
        <Box sx={{ pt: 3, pb: 2 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'secondary.main',
              fontWeight: 800,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              display: 'block',
              mb: 0.5,
            }}
          >
            Ardent KOSMOS
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 900, color: 'primary.main', lineHeight: 1.2 }}>
            About This System
          </Typography>
          <Box sx={{ width: 40, height: 3, bgcolor: 'secondary.main', borderRadius: 2, mt: 1.5, mb: 3 }} />

          <Typography variant="body2" sx={{ lineHeight: 1.75, color: 'text.secondary', fontSize: 14, mb: 2 }}>
            KOSMOS constructs a structured knowledge encyclopedia for curriculum standards.
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.75, color: 'text.secondary', fontSize: 14, mb: 2 }}>
            Each standard is examined through three complementary lenses: what the standard requires (
            <Box component="span" sx={{ fontWeight: 700, color: '#0369a1' }}>
              Requirement
            </Box>
            ), what must be learned to meet it (
            <Box component="span" sx={{ fontWeight: 700, color: '#7c3aed' }}>
              Content
            </Box>
            ), and what concepts and relationships exist within the domain (
            <Box component="span" sx={{ fontWeight: 700, color: '#065f46' }}>
              Knowledge
            </Box>
            ).
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.75, color: 'text.secondary', fontSize: 14, mb: 2 }}>
            KOSMOS makes the underlying knowledge explicit — discrete, named, and reviewable. The result is a validated
            knowledge layer that supports consistent curriculum design, instructional planning, and the development of
            learning resources.
          </Typography>

          {/* Research instrument callout */}
          <Box sx={{ bgcolor: 'primary.main', borderRadius: 2, px: 2.5, py: 2, mt: 1 }}>
            <Typography variant="body2" sx={{ color: '#fff', lineHeight: 1.65, fontSize: 14, fontStyle: 'italic' }}>
              This is a research instrument. Outputs are candidates, not conclusions.
            </Typography>
          </Box>
        </Box>

        {/* Extraction passes */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'primary.main', mb: 1.5 }}>
              Extraction Passes
            </Typography>
            {PASS_META.map((pass, idx) => (
              <Box key={pass.id}>
                <Box sx={{ display: 'flex', gap: 1.5, py: 1 }}>
                  <Typography sx={{ fontSize: 20 }}>{pass.icon}</Typography>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: pass.color, fontSize: 13 }}>
                      {pass.label} — {pass.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: 12, lineHeight: 1.5, display: 'block' }}
                    >
                      {pass.desc}
                    </Typography>
                  </Box>
                </Box>
                {idx < PASS_META.length - 1 && <Divider />}
              </Box>
            ))}
          </CardContent>
        </Card>

        {/* Knowledge types */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'primary.main', mb: 1 }}>
              Knowledge Types
            </Typography>
            {[
              { type: 'Fact', desc: 'A specific, singular piece of information that is true and verifiable.' },
              { type: 'Concept', desc: 'A category or class of things that share common attributes.' },
              { type: 'Procedure', desc: 'A sequence of steps or actions performed in a defined order by an agent.' },
              { type: 'Process', desc: 'A non-agent-driven sequence of stages or events that unfolds over time.' },
              {
                type: 'Principle',
                desc: 'A causal or relational statement that explains or predicts — when X, then Y.',
              },
            ].map((t) => (
              <Box key={t.type} sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 13 }}>
                  {t.type}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
                  {t.desc}
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>

        {/* Research context */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'primary.main', mb: 1 }}>
              Research Context
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.secondary', fontSize: 13 }}>
              This tool supports the study:{' '}
              <em>An Expert-Validated Analysis of Mathematics Standards Using AI-Assisted Learning Content Analysis</em>
              .
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.secondary', fontSize: 13, mt: 1 }}>
              The framework is grounded in M. David Merrill's Component Display Theory and investigates whether a
              compact set of knowledge types (fact, concept, procedure, process, principle) is sufficient to represent
              the knowledge embedded in mathematics standards.
            </Typography>
          </CardContent>
        </Card>

        {/* Phase roadmap */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: 'text.secondary',
                fontSize: 11,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                display: 'block',
                mb: 1.5,
              }}
            >
              Phase
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {PHASES.map((p) => (
                <Box key={p.n} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      flexShrink: 0,
                      mt: 0.1,
                      bgcolor: p.active ? 'primary.main' : 'grey.200',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography sx={{ fontSize: 10, fontWeight: 800, color: p.active ? '#fff' : 'text.disabled' }}>
                      {p.n}
                    </Typography>
                  </Box>
                  <Box sx={{ pt: 0.15 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: 13,
                        fontWeight: p.active ? 700 : 400,
                        color: p.active ? 'primary.main' : 'text.disabled',
                        lineHeight: 1.5,
                      }}
                    >
                      Phase {p.n} — {p.label}
                    </Typography>
                    {p.active && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: 'secondary.main',
                          bgcolor: '#fef3c7',
                          px: 0.75,
                          py: 0.15,
                          borderRadius: 0.5,
                          display: 'inline-block',
                          mt: 0.25,
                        }}
                      >
                        CURRENT
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  )
}
